import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { AUTH_MESSAGES, SERVER_MESSAGES, isServerMessage, mapAuthError } from 'layouts/auth';

/**
 * Gọi API cho wizard Quên mật khẩu (đặc tả §3.3). Cùng endpoint và cùng body với app HP Media
 * (hp-media-server-staging/src/auth/auth.controller.ts):
 *
 * 1. POST auth/forgot-password  { email }                       → 201 (mã 4 số gửi qua email)
 * 2. POST auth/verify/code-forgot { email, code }               → { status: 'ok' }
 * 3. POST auth/update-pass/forgot { email, code, newPassword, confirmNewPassword }
 *
 * Bước 1 và 3 đều so `code` với cột `users.code`, nên gửi lại mã thì mã cũ hết dùng được.
 */

export const FORGOT_MESSAGES = {
  rateLimited: 'Bạn thao tác quá nhanh. Vui lòng đợi khoảng 20 giây rồi thử lại.',
  codeWrong: 'Mã không đúng. Dùng mã trong email mới nhất hoặc bấm Gửi lại mã.',
  codeResent:
    'Nếu email có tài khoản đã xác minh, mã mới đã được gửi. Chỉ mã trong email mới nhất còn dùng được.',
} as const;

/**
 * Gửi (hoặc gửi lại) mã. 400 'User not verify' (email không có hoặc chưa xác minh) coi như đã
 * gửi: OWASP yêu cầu phản hồi giống nhau dù tài khoản có tồn tại hay không.
 */
export async function requestResetCode(email: string): Promise<void> {
  try {
    await AxiosUtils.post(API.forgotPassword, { email });
  } catch (error) {
    if (isServerMessage(error, SERVER_MESSAGES.USER_NOT_VERIFY)) return;
    throw error;
  }
}

/** Câu lỗi khi gửi mã thất bại. 5xx gần như luôn do máy chủ gửi email lỗi. */
export const mapSendCodeError = (error: unknown) =>
  mapAuthError(error, {
    rateLimited: FORGOT_MESSAGES.rateLimited,
    server: AUTH_MESSAGES.emailSendFailed,
  });

export async function verifyResetCode(email: string, code: string): Promise<void> {
  await AxiosUtils.post(API.verifyForgotCode, { email, code });
}

/**
 * DTO ChangePasswordUserForgotDto bắt buộc `confirmNewPassword` trùng `newPassword`. Trang
 * không có ô nhập lại (đặc tả §3.3) nên gửi cùng một giá trị.
 */
export async function resetPassword(email: string, code: string, newPassword: string) {
  await AxiosUtils.post(API.updatePasswordForgot, {
    email,
    code,
    newPassword,
    confirmNewPassword: newPassword,
  });
}

export const isCodeWrong = (error: unknown) => isServerMessage(error, SERVER_MESSAGES.CODE_WRONG);

/** Câu lỗi chung cho bước 2 và 3 (sau khi đã loại trừ "sai mã"). */
export const mapStepError = (error: unknown) =>
  mapAuthError(error, { rateLimited: FORGOT_MESSAGES.rateLimited });
